import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { PhotoIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Product, Category, FoodSearchImage } from '../types';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import Cropper, { Area } from 'react-easy-crop';

// Configuration constants
const MAX_PRODUCT_IMAGES = 1;

interface ProductFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (product: Product, mode: 'create' | 'update') => void;
  product?: Product;
  onNavigateToCategories?: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({ isOpen, onClose, onSuccess, product, onNavigateToCategories }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showError, showSuccess } = useToast();
  const [selectedImages, setSelectedImages] = useState<string[]>(product?.images || []);
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [parentCategory, setParentCategory] = useState('');
  const [childCategory, setChildCategory] = useState('');
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingImagesLoading, setExistingImagesLoading] = useState(false);
  const [existingImagesError, setExistingImagesError] = useState('');
  const [deletingImage, setDeletingImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [pendingImageName, setPendingImageName] = useState('');
  const [pendingImageMime, setPendingImageMime] = useState('image/jpeg');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [foodSearchQuery, setFoodSearchQuery] = useState('');
  const [foodSearchResults, setFoodSearchResults] = useState<FoodSearchImage[]>([]);
  const [foodSearchLoading, setFoodSearchLoading] = useState(false);
  const [foodSearchError, setFoodSearchError] = useState('');
  const [importingFoodImageId, setImportingFoodImageId] = useState<number | null>(null);

  const handleImageError = (url: string) => {
    setImageErrors(prev => new Set(prev).add(url));
  };
  const [portions, setPortions] = useState<Array<{ name: string; price: number | undefined; offerPrice: number | undefined }>>(
    (product?.portions || []).map(portion => ({
      ...portion,
      // offerPrice is ONLY set through manual entry - never automatically from price
      offerPrice: portion.offerPrice || undefined
    }))
  );
  const [usePortionPricing, setUsePortionPricing] = useState<boolean>(
    Boolean(product?.portions && product.portions.length > 0)
  );

  // Portions management functions
  const addPortion = () => {
    setPortions([...portions, { name: '', price: undefined, offerPrice: undefined }]);
  };

  const removePortion = (index: number) => {
    setPortions(portions.filter((_, i) => i !== index));
  };

  const updatePortion = (index: number, field: 'name' | 'price' | 'offerPrice', value: string | number) => {
    const newPortions = [...portions];
    if (field === 'name') {
      newPortions[index] = { ...newPortions[index], [field]: value as string };
    } else {
      // For price and offerPrice - ONLY set through manual entry, never automatically
      // Convert to number or undefined (don't default to 0 - preserve manual entry only)
      let numValue: number | undefined;
      if (typeof value === 'string') {
        if (value === '') {
          numValue = undefined;
        } else {
          const parsed = parseFloat(value);
          numValue = isNaN(parsed) ? undefined : parsed;
        }
      } else {
        numValue = value;
      }
      newPortions[index] = { ...newPortions[index], [field]: numValue };
    }
    setPortions(newPortions);
  };

  // Format category name for display
  const formatCategoryName = (categoryName: string) => {
    if (!categoryName) return '';
    console.log('Formatting category name:', categoryName);
    // Remove leading and trailing slashes, then split
    const cleaned = categoryName.replace(/^\/+|\/+$/g, '');
    const formatted = cleaned
      .split('/')
      .filter(Boolean)
      .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
      .join(' > ');
    console.log('Formatted result:', formatted);
    return formatted;
  };

  const genericPhrases = [
    "A great choice for everyone.",
    "High-quality and best-selling item.",
    "Customer favorite. Try it today!",
    "Trusted by many happy customers.",
    "Top-rated item in our collection.",
    "Affordable and reliable choice.",
    "Popular choice among customers.",
    "Great value for the price."
  ];
  const getRandomPhrase = () => {
    return genericPhrases[Math.floor(Math.random() * genericPhrases.length)];
  };

  // Auto-generate description based on name and category
  const autoDescription = (name: string, category: string) => {
    const cat = category.toLowerCase();
    
    if (cat.includes("biriyani")) {
      return `${name} - Aromatic rice cooked with spices and tender meat, served with raita and pickle.`;
    } else if (cat.includes("seafood")) {
      return `${name} - Fresh catch from the coast, cooked in Kerala-style spices for a rich and tangy flavor.`;
    } else if (cat.includes("mandi") || cat.includes("manthi")) {
      return `${name} - Arabian-style rice dish served with juicy grilled meat and special dips.`;
    } else if (cat.includes("bread") || cat.includes("porotta")) {
      return `${name} - Soft, flaky flatbread perfect with curries.`;
    } else if (cat.includes("chicken")) {
      return `${name} - Tender chicken cooked with aromatic spices and herbs for authentic flavor.`;
    } else if (cat.includes("beef")) {
      return `${name} - Rich and flavorful beef dish prepared with traditional spices.`;
    } else if (cat.includes("mutton")) {
      return `${name} - Succulent mutton cooked to perfection with aromatic spices.`;
    } else if (cat.includes("fish")) {
      return `${name} - Fresh fish prepared with Kerala spices and coconut for authentic taste.`;
    } else if (cat.includes("rice")) {
      return `${name} - Fragrant rice dish cooked with spices and fresh ingredients.`;
    } else if (cat.includes("curry")) {
      return `${name} - Rich and flavorful curry made with authentic spices and fresh ingredients.`;
    } else if (cat.includes("soup")) {
      return `${name} - Hearty soup prepared with fresh vegetables and aromatic spices.`;
    } else if (cat.includes("salad")) {
      return `${name} - Fresh and healthy salad with crisp vegetables and light dressing.`;
    } else if (cat.includes("dessert")) {
      return `${name} - Sweet and delicious dessert made with traditional ingredients.`;
    } else if (cat.includes("beverage") || cat.includes("drink")) {
      return `${name} - Refreshing beverage perfect to complement your meal.`;
    } else {
      return `${name} - ${getRandomPhrase()}`;
    }
  };

  // Handle auto-generation of description
  const handleAutoDescription = () => {
    const productName = watch('name');
    const selectedCategory = childCategory || parentCategory;
    
    if (!productName.trim()) {
      showError('Auto-Generate Failed', 'Please enter a product name first');
      return;
    }
    
    if (!selectedCategory) {
      showError('Auto-Generate Failed', 'Please select a category first');
      return;
    }
    
    const generatedDescription = autoDescription(productName, selectedCategory);
    setValue('longDescription', generatedDescription);
    // showSuccess('Description Generated', 'Auto-generated description based on name and category');
  };

  const hasHierarchy = categories.some(cat => (cat as Category).parent || Array.isArray((cat as Category).childrens));

  // Determine parent categories. If no hierarchy metadata, treat all categories as parents.
  const parentCategories = hasHierarchy
    ? categories.filter(cat => (cat as Category).parent === 'root')
    : categories;

  // Determine child categories when hierarchy exists.
  const selectedParent = hasHierarchy
    ? categories.find(cat => cat.name === parentCategory)
    : undefined;
  const childNames = Array.isArray((selectedParent as Category | undefined)?.childrens)
    ? ((selectedParent as Category).childrens as string[])
    : [];
  const childCategories = hasHierarchy
    ? categories.filter(cat => childNames.includes(cat.name))
    : [];

  useEffect(() => {
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        // First, try to load from localStorage
        const storedCategories = localStorage.getItem('categories');
        if (storedCategories) {
          try {
            const parsedCategories = JSON.parse(storedCategories);
            let categoriesArray: Category[] = [];
            
            // Handle both direct array and response format
            if (Array.isArray(parsedCategories)) {
              categoriesArray = parsedCategories;
            } else if (parsedCategories.data && Array.isArray(parsedCategories.data)) {
              categoriesArray = parsedCategories.data;
            } else if (parsedCategories.doc && Array.isArray(parsedCategories.doc)) {
              categoriesArray = parsedCategories.doc;
            }
            
            if (categoriesArray.length > 0) {
              const activeCategories = categoriesArray.filter((cat: Category) => {
                const status = (cat as Category).status;
                if (status === undefined || status === null) return true;
                if (typeof status === 'number') return status === 1;
                if (typeof status === 'string') return status === '1';
                return Boolean(status);
              });
              setCategories(activeCategories);
              setCategoriesError('');
              setCategoriesLoading(false);
              return;
            }
          } catch (error) {
            console.error('Failed to parse categories from localStorage:', error);
          }
        }
        
        // If not in localStorage or empty, fetch from API
        const res = await apiService.getCategories();
        if (res.status) {
          const rawCategories = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.doc)
            ? res.doc
            : [];

          if (rawCategories.length > 0) {
            const activeCategories = rawCategories.filter((cat: Category) => {
              const status = (cat as Category).status;
              if (status === undefined || status === null) return true;
              if (typeof status === 'number') return status === 1;
              if (typeof status === 'string') return status === '1';
              return Boolean(status);
            });
            setCategories(activeCategories);
            setCategoriesError('');
            // Store in localStorage for future use
            localStorage.setItem('categories', JSON.stringify(activeCategories));
          } else {
            setCategories([]);
            setCategoriesError('No categories found. Please add a category first.');
          }
        } else {
          setCategoriesError(res.error || res.message || 'Failed to load categories');
        }
      } catch (error) {
        setCategoriesError('Failed to load categories');
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []);

  // useEffect(() => {
  //   setShippingTypesLoading(true);
  //   apiService.getShippingTypes()
  //     .then((res) => {
  //       const types = Array.isArray(res.doc) ? res.doc.filter((type: ShippingType) => type.status === 1) : [];
  //       setShippingTypes(types);
  //       if (!Array.isArray(res.doc)) {
  //         setShippingTypesError(res.error || 'Failed to load shipping types');
  //       }
  //     })
  //     .catch(() => setShippingTypesError('Failed to load shipping types'))
  //     .finally(() => setShippingTypesLoading(false));
  // }, []);

  const readFileAsDataURL = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const createImage = useCallback((url: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  }, []);

  const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180;

  const rotateSize = (width: number, height: number, rotationValue: number) => {
    const rotRad = getRadianAngle(rotationValue);

    return {
      width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
  };

  const getCroppedBlob = useCallback(
    async (imageSrc: string, pixelCrop: Area, mimeType: string, rotationValue: number) => {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to create canvas context');
      }

      const rotRad = getRadianAngle(rotationValue);
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotationValue);

      canvas.width = bBoxWidth;
      canvas.height = bBoxHeight;

      ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      ctx.rotate(rotRad);
      ctx.translate(-image.width / 2, -image.height / 2);
      ctx.drawImage(image, 0, 0);

      const croppedCanvas = document.createElement('canvas');
      const croppedCtx = croppedCanvas.getContext('2d');

      if (!croppedCtx) {
        throw new Error('Failed to create cropped canvas context');
      }

      croppedCanvas.width = pixelCrop.width;
      croppedCanvas.height = pixelCrop.height;

      croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      return new Promise<Blob>((resolve, reject) => {
        croppedCanvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(blob);
          },
          mimeType === 'image/png' ? 'image/png' : 'image/jpeg',
          mimeType === 'image/png' ? undefined : 0.92
        );
      });
    },
    [createImage]
  );

  const resetCropState = useCallback(() => {
    setShowCropper(false);
    setCropImageSrc(null);
    setCroppedAreaPixels(null);
    setPendingImageName('');
    setPendingImageMime('image/jpeg');
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
  }, []);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixelsValue: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleCropCancel = () => {
    resetCropState();
  };

  const loadExistingImages = useCallback(async () => {
    setExistingImagesLoading(true);
    setExistingImagesError('');
    try {
      // First, try to load from localStorage
      const storedProductImages = localStorage.getItem('productImages');
      if (storedProductImages) {
        try {
          const parsedImages = JSON.parse(storedProductImages);
          let imagesArray: string[] = [];
          
          // Handle both direct array and response format
          if (Array.isArray(parsedImages)) {
            imagesArray = parsedImages;
          } else if (parsedImages.data && Array.isArray(parsedImages.data)) {
            // If it's an array of objects with 'path' property
            imagesArray = parsedImages.data.map((img: { path: string } | string) => 
              typeof img === 'string' ? img : img.path
            );
          }
          
          if (imagesArray.length > 0) {
            setExistingImages(imagesArray);
            setExistingImagesLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to parse productImages from localStorage:', error);
        }
      }
      
      // If not in localStorage or empty, fetch from API
      const res = await apiService.getImages('productImage');
      if (res.status && Array.isArray(res.data)) {
        const imagePaths = res.data.map(img => img.path);
        setExistingImages(imagePaths);
        // Store in localStorage for future use
        localStorage.setItem('productImages', JSON.stringify(imagePaths));
      } else {
        setExistingImagesError(res.message || 'Failed to load images');
      }
    } catch (error) {
      setExistingImagesError('Failed to load images');
    } finally {
      setExistingImagesLoading(false);
    }
  }, []);

  // Load existing images
  useEffect(() => {
    loadExistingImages();
  }, [loadExistingImages]);

  // Reset childCategory if parent changes
  useEffect(() => {
    setChildCategory('');
  }, [parentCategory]);



  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<Product>({
    defaultValues: product || {
      name: '',
      images: [],
      substractStock: false,
      minimumQuantity: 1,
      condition: 'new',
      todayDeals: false,
      outofstock:false,
      hideFromUser:false,
      extraField: {},
      longDescription: '',
      category: '',
    },
    mode: 'onChange',
  });

  // Handle browser back button to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = () => {
      onClose();
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // Reset form and state when product changes (switching between add/edit modes)
  useEffect(() => {
    if (product) {
      // Reset form with product data
      reset(product);
      // Set images
      setSelectedImages(product.images || []);
      // Set portions - offerPrice is ONLY set through manual entry, never automatically from price
      setPortions((product.portions || []).map(portion => ({
        ...portion,
        offerPrice: portion.offerPrice || undefined
      })));
      // Set pricing toggle based on whether product has portions
      setUsePortionPricing(Boolean(product.portions && product.portions.length > 0));
    } else {
      // Reset form for new product
      reset({
        name: '',
        images: [],
        substractStock: false,
        minimumQuantity: 1,
        condition: 'new',
        todayDeals: false,
        outofstock: false,
        hideFromUser:false,
        extraField: {},
        longDescription: '',
        category: ''
      });
      // Reset state
      setSelectedImages([]);
      setPortions([]);
      setUsePortionPricing(false);
      setParentCategory('');
      setChildCategory('');
    }
  }, [product, reset]);

  // Set category dropdowns when editing a product
  useEffect(() => {
    if (product && product.category && categories.length > 0) {
      const productCategoryName = Array.isArray(product.category)
        ? product.category[0]
        : product.category;

      const category = categories.find(cat => cat.name === productCategoryName);
      if (!category) {
        console.log('Category not found:', productCategoryName);
        return;
      }

      if (!hasHierarchy || !category.parent || category.parent === 'root') {
        setParentCategory(category.name);
        setChildCategory('');
      } else {
        const parent = categories.find(cat => cat.name === category.parent);
        if (parent) {
          setParentCategory(parent.name);
          setChildCategory(category.name);
        }
      }
    }
  }, [product, categories, hasHierarchy]);

  // Debug: Log current dropdown values
  useEffect(() => {
    console.log('Current dropdown values:', { parentCategory, childCategory });
  }, [parentCategory, childCategory]);


  // Clear portions when switching to single pricing
  useEffect(() => {
    if (!usePortionPricing) {
      setPortions([]);
    }
  }, [usePortionPricing]);

  // Get extra fields from selected category only (not from parent)
  const selectedCategory = categories.find(cat => cat.name === (childCategory || parentCategory));
  const extraFields = selectedCategory?.extraField || [];

  // In the form submission, set the category to childCategory if selected, otherwise parentCategory
  const onSubmit = async (data: Product) => {
    setIsLoading(true);

    // Validate category selection
    if (!parentCategory && !childCategory) {
      showError('Validation Error', 'Please select a category');
      setIsLoading(false);
      return;
    }

    // Validate price fields if not using portion pricing (required, 0 allowed)
    if (!usePortionPricing) {
      if (data.price === undefined || data.price === null) {
        showError('Validation Error', 'Price is required when using single pricing');
        setIsLoading(false);
        return;
      }
      if (data.offerPrice === undefined || data.offerPrice === null) {
        showError('Validation Error', 'Offer price is required when using single pricing');
        setIsLoading(false);
        return;
      }
    }

    // Validate extra fields if they exist
    if (extraFields.length > 0) {
      const missingExtraFields = extraFields.filter(field => {
        const fieldValue = data.extraField?.[field];
        return !fieldValue || fieldValue.trim() === '';
      });
      
      if (missingExtraFields.length > 0) {
        showError('Validation Error', `Please fill in all required extra fields: ${missingExtraFields.join(', ')}`);
        setIsLoading(false);
        return;
      }
    }

    // Validate portions if portion pricing is enabled
    if (usePortionPricing) {
      const invalidPortionDecimals = portions.some(portion => {
        if (portion.name.trim() === '') return false;
        const checkDecimals = (val?: number) => {
          if (val === undefined || val === null) return false;
          const raw = val.toString();
          const decimals = (raw.split('.')[1] || '').length;
          return decimals > 2;
        };
        return checkDecimals(portion.price) || checkDecimals(portion.offerPrice);
      });

      if (invalidPortionDecimals) {
        showError('Validation Error', 'Maximum allowed decimal part is 2 for portion price and offer price');
        setIsLoading(false);
        return;
      }

      const validPortions = portions.filter(portion => 
        portion.name.trim() !== '' && 
        typeof portion.price === 'number' && 
        portion.price > 0 && 
        typeof portion.offerPrice === 'number' && 
        portion.offerPrice > 0
      );
      if (validPortions.length === 0) {
        showError('Validation Error', 'Please add at least one portion with a name, price, and offer price when using portion pricing');
        setIsLoading(false);
        return;
      }
    }

    // Always set condition to 'new'
    data.condition = 'new';
    // Set category from cascading dropdown
    data.category = childCategory || parentCategory;

    // Price and offerPrice are ONLY set through manual entry - never automatically calculated
    // When using portion pricing, set to 0 (portions have their own prices)
    // When NOT using portion pricing, preserve the manually entered values from the form
    if (usePortionPricing) {
      data.price = 0;
      data.offerPrice = 0;
    }
    // When NOT using portion pricing, data.price and data.offerPrice come directly from form inputs
    // Do NOT modify them - they are manually entered only

    try {
      // Remove extraField if it's empty or doesn't exist
      const { extraField, ...dataWithoutExtraField } = data;
      
      // Filter out empty values from extraField
      const filteredExtraField = extraField ? 
        Object.fromEntries(
          Object.entries(extraField).filter(([_, value]) => value && value.trim() !== '')
        ) : {};
      
      const hasExtraFieldValues = Object.keys(filteredExtraField).length > 0;
      
      // Construct productData - ensure price and offerPrice are ONLY from manual entry
      const productData = {
        ...dataWithoutExtraField,
        // Explicitly preserve manually entered price and offerPrice when NOT using portion pricing
        // These values come directly from form inputs - never automatically calculated
        price: usePortionPricing ? 0 : (data.price || 0),
        offerPrice: usePortionPricing ? 0 : (data.offerPrice || 0),
        ...(hasExtraFieldValues && { extraField: filteredExtraField }),
        images: selectedImages,
        ...(usePortionPricing && { 
          portions: portions
            .filter(portion => 
              portion.name.trim() !== '' && 
              typeof portion.price === 'number' && 
              portion.price > 0 && 
              typeof portion.offerPrice === 'number' && 
              portion.offerPrice > 0
            )
            .map(portion => ({
              name: portion.name,
              price: portion.price as number,
              offerPrice: portion.offerPrice as number
            }))
        }),
      };

      // If editing but product doesn't have _id, show error
      if (product && !product._id) {
        showError('Error', 'Product ID not found. Please close and try editing again after the page reloads.');
        setIsLoading(false);
        return;
      }

      const response = product
        ? await apiService.updateProduct(product._id!, productData)
        : await apiService.createProduct(productData);

      if (response.status == true) {
        const deriveProductFromResponse = (): Product | undefined => {
          const docCandidate =
            response.doc && !Array.isArray(response.doc) ? (response.doc as Product) : undefined;
          const dataCandidate =
            response.data && !Array.isArray(response.data) ? (response.data as Product) : undefined;
          return docCandidate ?? dataCandidate;
        };

        const resolvedProduct =
          deriveProductFromResponse() ??
          ({
            ...productData,
            ...(product ? { _id: product._id } : {}),
          } as Product);

        // if (!resolvedProduct._id) {
        //   resolvedProduct._id = product?._id ?? `temp-${Date.now()}`;
        // }

        if (
          resolvedProduct &&
          resolvedProduct.category &&
          !Array.isArray((resolvedProduct as any).category)
        ) {
          (resolvedProduct as any).category = [(resolvedProduct as any).category];
        }

        showSuccess(
         product ? 'Product updated successfully' : 'Product created successfully'
        );
        onSuccess(resolvedProduct, product ? 'update' : 'create');
        reset();
        onClose();
      } else {
        showError('Save Failed', response.message || 'Failed to save product');
      }
    } catch (err) {
      showError('Save Failed', 'An error occurred while saving the product');
    } finally {
      setIsLoading(false);
    }
  };

  const applyUploadedImagePath = (imagePath: string, closeModal = true) => {
    localStorage.removeItem('productImages');
    setImageErrors((prev) => {
      const newSet = new Set(prev);
      newSet.delete(imagePath);
      return newSet;
    });
    setExistingImages((prev) => (prev.includes(imagePath) ? prev : [...prev, imagePath]));
    if (selectedImages.length < MAX_PRODUCT_IMAGES) {
      setSelectedImages((prev) => [...prev, imagePath]);
      showSuccess('Image Uploaded', 'Image uploaded and added to product');
    } else {
      showSuccess(
        'Image Uploaded',
        `Image uploaded to library. You can select it later (max ${MAX_PRODUCT_IMAGES} image(s) per product)`
      );
    }
    if (closeModal) {
      setShowImageModal(false);
    }
  };

  const openImageModal = () => {
    const name = (watch('name') || product?.name || '').trim();
    setFoodSearchQuery(name);
    setFoodSearchResults([]);
    setFoodSearchError('');
    setShowImageModal(true);
  };

  const handleFoodImageSearch = async () => {
    const query = foodSearchQuery.trim();
    if (!query) {
      showError('Search Required', 'Enter a food name to search for images');
      return;
    }
    setFoodSearchLoading(true);
    setFoodSearchError('');
    setFoodSearchResults([]);
    try {
      const result = await apiService.searchFoodImages(query);
      if (result.status && Array.isArray(result.data)) {
        setFoodSearchResults(result.data);
        if (result.data.length === 0) {
          setFoodSearchError('No images found. Try a different search term.');
        }
      } else {
        setFoodSearchError(result.message || 'Failed to search images');
      }
    } catch {
      setFoodSearchError('Failed to search images. Please try again.');
    } finally {
      setFoodSearchLoading(false);
    }
  };

  const openImageForCropping = async (file: File) => {
    try {
      setUploadingImage(true);
      const dataUrl = await readFileAsDataURL(file);
      setCropImageSrc(dataUrl);
      setPendingImageName(file.name || 'product-image.jpg');
      setPendingImageMime(file.type || 'image/jpeg');
      setZoom(1);
      setRotation(0);
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels(null);
      setShowCropper(true);
    } catch (err) {
      console.error('Image preview error:', err);
      showError('Image Preview Failed', 'Failed to load image for cropping. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFoodImageSelect = async (item: FoodSearchImage) => {
    setImportingFoodImageId(item.id);
    try {
      const response = await fetch(item.url);
      if (!response.ok) {
        throw new Error('Failed to download image');
      }
      const blob = await response.blob();
      const mimeType = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
      const extension = mimeType === 'image/png' ? 'png' : 'jpg';
      const file = new File([blob], `food-${item.id}.${extension}`, { type: mimeType });
      await openImageForCropping(file);
    } catch (err) {
      console.error('Food image import error:', err);
      showError(
        'Import Failed',
        'Could not load the selected image for cropping. Try another image or upload from your device.'
      );
    } finally {
      setImportingFoodImageId(null);
    }
  };

  const handleImageSelect = (imageUrl: string) => {
    if (!selectedImages.includes(imageUrl)) {
      if (selectedImages.length >= MAX_PRODUCT_IMAGES) {
        showError('Image Limit Reached', `Maximum ${MAX_PRODUCT_IMAGES} images allowed per product`);
        return;
      }
      setSelectedImages([...selectedImages, imageUrl]);
    }
    setShowImageModal(false);
  };

  const handleImageRemove = (imageUrl: string) => {
    setSelectedImages(selectedImages.filter(img => img !== imageUrl));
  };

  const handleImageDelete = async (imagePath: string) => {
    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeletingImage(imagePath);
    try {
      const result = await apiService.deleteImage(imagePath);
      
      if (result.status) {
        // Clear localStorage to force fresh fetch on next load
        localStorage.removeItem('productImages');
        // Remove from existing images
        setExistingImages(existingImages.filter(img => img !== imagePath));
        // Remove from selected images if it was selected
        setSelectedImages(selectedImages.filter(img => img !== imagePath));
        showSuccess('Image Deleted', 'Image deleted successfully');
      } else {
        showError('Delete Failed', result.message || 'Failed to delete image');
      }
    } catch (err) {
      console.error('Image delete error:', err);
      showError('Delete Failed', 'Failed to delete image. Please try again.');
    } finally {
      setDeletingImage(null);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await openImageForCropping(file);

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels) {
      return;
    }

    // Allow unlimited uploads - no limit check here
    // Uploaded images go to the library, selection is limited separately

    setUploadingImage(true);
    try {
      const croppedBlob = await getCroppedBlob(cropImageSrc, croppedAreaPixels, pendingImageMime, rotation);
      const fileExtension = pendingImageMime === 'image/png' ? 'png' : 'jpg';
      const safeFileName = pendingImageName || `product-image.${fileExtension}`;
      const croppedFile = new File([croppedBlob], safeFileName, { type: croppedBlob.type });

      const result = await apiService.uploadImage(croppedFile, 'productImage');

      if (
        result.status === true &&
        result.data &&
        typeof (result.data as { path?: string }).path === 'string'
      ) {
        applyUploadedImagePath((result.data as { path: string }).path);
        resetCropState();
      } else {
        showError('Upload Failed', result.message || 'Failed to upload image. Please try again.');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      const errorMessage = err instanceof Error ? err.message : (err as any)?.message || 'Failed to upload image. Please try again.';
      showError('Upload Failed', errorMessage);
    } finally {
      setUploadingImage(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
      <div 
        onClick={() => {
          if (window.history.state?.modalOpen) {
            window.history.back();
          }
          onClose();
        }} 
        className="absolute inset-0" 
      />
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {product ? 'Edit Product' : 'Add New Product'}
            </h3>
            <button 
              onClick={() => {
                if (window.history.state?.modalOpen) {
                  window.history.back();
                }
                onClose();
              }} 
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  {...register('name', { required: 'name is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Category selection (cascading dropdown) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Category *
                  </label>
                  {onNavigateToCategories && (
                    <button
                      type="button"
                      onClick={onNavigateToCategories}
                      className="text-xs text-orange-600 hover:text-orange-800 font-medium flex items-center"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add Category
                    </button>
                  )}
                </div>
                {categoriesLoading ? (
                  <div className="text-sm text-gray-500">Loading categories...</div>
                ) : categoriesError ? (
                  <div className="text-sm text-red-600">{categoriesError}</div>
                ) : (
                  <>
                    <select
                      value={parentCategory}
                      onChange={e => setParentCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 mb-2"
                    >
                      <option value="">Select a category *</option>
                      {parentCategories.map(cat => (
                        <option key={cat.name} value={cat.name}>{formatCategoryName(cat.name)}</option>
                      ))}
                    </select>
                    {childNames.length > 0 && (
                      <select
                        value={childCategory}
                        onChange={e => setChildCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select a subcategory</option>
                        {childCategories.map(cat => (
                          <option key={cat.name} value={cat.name}>{formatCategoryName(cat.name)}</option>
                        ))}
                      </select>
                    )}
                    {parentCategories.length === 0 && !categoriesLoading && (
                      <div className="text-sm text-gray-500 mt-2">
                        No categories found. 
                        {onNavigateToCategories && (
                          <button
                            type="button"
                            onClick={onNavigateToCategories}
                            className="text-orange-600 hover:text-orange-800 font-medium ml-1"
                          >
                            Create your first category
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
                {/* Validation error */}
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                )}
              </div>
            </div>

            {/* Pricing Toggle Section */}
            <div className="col-span-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3 sm:space-y-0">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Pricing Type</h4>
                  <p className="text-xs text-gray-600">
                  Choose between Single Product Price or Multiple Variant Pricing
                  </p>
                </div>
                <div className="flex items-center justify-center sm:justify-end space-x-3">
                  <span className={`text-sm font-medium ${!usePortionPricing ? 'text-orange-600' : 'text-gray-500'}`}>
                    Single Price
                  </span>
                  <button
                    type="button"
                    onClick={() => setUsePortionPricing(!usePortionPricing)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      usePortionPricing ? 'bg-orange-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        usePortionPricing ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${usePortionPricing ? 'text-orange-600' : 'text-gray-500'}`}>
                    Variant Pricing
                  </span>
                </div>
              </div>
            </div>

            {/* Price fields - only show when not using portion pricing */}
            {!usePortionPricing && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price *
                    </label>
                    <input
                      {...register('price', {
                        required: !usePortionPricing ? 'Price is required' : false,
                        validate: (value: any) => {
                          if (usePortionPricing) return true;
                          if (value === undefined || value === null || value === '') {
                            return 'Price is required';
                          }
                          const raw = String(value);
                          const num = Number(raw);
                          if (Number.isNaN(num)) return 'Invalid price';
                          const decimals = (raw.split('.')[1] || '').length;
                          if (decimals > 2) return 'Maximum allowed decimal part is 2';
                          return true;
                        }
                      })}
                      type="number"
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        errors.price ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter price"
                    />
                    {errors.price && (
                      <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offer Price *
                    </label>
                    <input
                      {...register('offerPrice', {
                        required: !usePortionPricing ? 'Offer price is required' : false,
                        validate: (value: any) => {
                          if (usePortionPricing) return true;
                          if (value === undefined || value === null || value === '') {
                            return 'Offer price is required';
                          }
                          const raw = String(value);
                          const num = Number(raw);
                          if (Number.isNaN(num)) return 'Invalid offer price';
                          const decimals = (raw.split('.')[1] || '').length;
                          if (decimals > 2) return 'Maximum allowed decimal part is 2';
                          return true;
                        }
                      })}
                      type="number"
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        errors.offerPrice ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter offer price"
                    />
                    {errors.offerPrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.offerPrice.message}</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use same value for price and offer price if no discount.
                </p>
              </>
            )}

            {/* Portions Section */}
            {usePortionPricing && (
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Variants & Pricing
                  </label>
                  <button
                    type="button"
                    onClick={addPortion}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Variant
                  </button>
                </div>
                
                {portions.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <p className="text-sm text-gray-500">No variants added yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click "Add Variant" to create different variants with pricing</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Portion Headers */}
                    <div className="grid grid-cols-12 gap-3 px-3 py-2 bg-gray-100 rounded-lg text-xs font-medium text-gray-700">
                      <div className="col-span-4 md:col-span-5">Variant Name</div>
                      <div className="col-span-3">Regular Price</div>
                      <div className="col-span-3">Offer Price</div>
                      <div className="col-span-1">Action</div>
                    </div>
                    
                    {portions.map((portion, index) => (
                      <div key={index} className="grid grid-cols-12 gap-3 items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="col-span-4 md:col-span-5">
                          <input
                            type="text"
                            value={portion.name}
                            onChange={(e) => updatePortion(index, 'name', e.target.value)}
                            placeholder="e.g., Small, Medium, Large"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={portion.price ?? ''}
                            onChange={(e) => updatePortion(index, 'price', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={portion.offerPrice ?? ''}
                            onChange={(e) => updatePortion(index, 'offerPrice', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <button
                            type="button"
                            onClick={() => removePortion(index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                            title="Remove portion"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {portions.length > 0 && (
                  <>
                    <p className="mt-2 text-xs text-gray-500">
                      Only variants with names, regular prices, and offer prices will be saved.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Use same value for price and offer price if no discount.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Inventory Tracking and Quantity on separate rows */}
{/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <div className="col-span-2">
    <label className="flex items-start">
      <input
        {...register('substractStock')}
        type="checkbox"
        className="mt-1 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
      />
      <div className="ml-2">
        <span className="text-sm font-medium text-gray-800">Enable Stock Tracking</span>
        <p className="text-sm text-gray-500">
          Automatically mark items as “Out of Stock” when quantity is zero.
        </p>
      </div>
    </label>
  </div>
</div> */}

            {/* {substractStock && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    {...register('quantity', {
                      required: substractStock ? 'Quantity is required when inventory tracking is enabled' : false,
                      min: { value: 0, message: 'Quantity must be 0 or greater' },
                    })}
                    type="number"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      errors.quantity ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter quantity"
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                  )}
                </div>
              </div>
            )} */}

            {/* All other fields start from a new row below */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Quantity *
                </label>
                <input
                  {...register('minimumQuantity', {
                    required: 'Minimum quantity is required',
                    min: { value: 1, message: 'Minimum quantity must be at least 1' },
                  })}
                  type="number"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    errors.minimumQuantity ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter minimum quantity"
                />
                {errors.minimumQuantity && (
                  <p className="mt-1 text-sm text-red-600">{errors.minimumQuantity.message}</p>
                )}
              </div> */}

              {/* Remove the Condition field from the form UI */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition
                </label>
                <select
                  {...register('condition')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="new">New</option>
                  <option value="used">Used</option>
                  <option value="refurbished">Refurbished</option>
                </select>
              </div> */}

              {/* Shipping Charges for each shipping type */}
              {/* <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shipping Charges *
                </label>
                {shippingTypesLoading ? (
                  <div className="text-sm text-gray-500">Loading shipping types...</div>
                ) : shippingTypesError ? (
                  <div className="text-sm text-red-600">{shippingTypesError}</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shippingTypes.map((type) => (
                      <div key={type.name}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {type.name} *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...(register as any)(`shippingCharges.${type.name}`, {
                            required: `Shipping charge for ${type.name} is required`,
                            min: { value: 0, message: 'Shipping charge must be 0 or greater' },
                          })}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                            (errors.shippingCharges as any)?.[type.name] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter shipping charge"
                        />
                        {(errors.shippingCharges as any)?.[type.name] && (
                          <p className="mt-1 text-xs text-red-600">{(errors.shippingCharges as any)[type.name]?.message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div> */}

              {/* Remove or comment out the Manufacture ID field from the form UI */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacture ID
                </label>
                <input
                  {...register('manufactureId')}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter manufacture ID"
                />
              </div> */}

              {/* Dynamic Extra Fields from Category */}
              {extraFields.length > 0 && (
                <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {extraFields.map((field: string) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.charAt(0).toUpperCase() + field.slice(1)} *
                      </label>
                      <input
                        {...register(`extraField.${field}`, {
                          required: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
                        })}
                        type="text"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                          (errors.extraField as any)?.[field] ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder={`Enter ${field}`}
                      />
                      {(errors.extraField as any)?.[field] && (
                        <p className="mt-1 text-sm text-red-600">{(errors.extraField as any)[field]?.message}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Remove or comment out the Return Time and Warranty Time fields from the form UI */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Return Time (Days)
                </label>
                <input
                  {...register('returnTimeInDays', {
                    min: { value: 0, message: 'Return time must be positive' }
                  })}
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Warranty Time (Days)
                </label>
                <input
                  {...register('warrentyTimeInDays', {
                    min: { value: 0, message: 'Warranty time must be positive' }
                  })}
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0"
                />
              </div> */}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Images
              </label>
              <div className="space-y-3">
                {selectedImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        {imageErrors.has(image) ? (
                          <div className="w-full h-20 flex items-center justify-center bg-gray-200 rounded-lg border border-gray-300">
                            <span className="text-xs text-gray-500">Image not found</span>
                          </div>
                        ) : (
                          <img
                            src={image}
                            alt={`Product ${index + 1}`}
                            onError={() => handleImageError(image)}
                            className="w-full h-20 object-cover rounded-lg border border-gray-300"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => handleImageRemove(image)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1  group-hover:opacity-100 transition-opacity"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={openImageModal}
                  className="flex items-center justify-center w-full h-20 border-2 border-dashed rounded-lg transition-colors border-gray-300 hover:border-orange-500 hover:bg-orange-50"
                >
                  <div className="text-center">
                    <PhotoIcon className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {selectedImages.length >= MAX_PRODUCT_IMAGES 
                        ? `Upload or Select Images (${selectedImages.length}/${MAX_PRODUCT_IMAGES} selected)` 
                        : 'Add Images'}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Description *
                </label>
                <button
                  type="button"
                  onClick={handleAutoDescription}
                  className="px-3 py-1.5 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!watch('name') || (!childCategory && !parentCategory)}
                  title={!watch('name') ? 'Enter product name first' : (!childCategory && !parentCategory) ? 'Select category first' : 'Auto-generate description based on name and category'}
                >
                  Auto-Generate
                </button>
              </div>
              <textarea
                {...register('longDescription', { required: 'Product description is required' })}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  errors.longDescription ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter detailed product description or click 'Auto-Generate' to create a generic description"
              />
              {errors.longDescription && (
                <p className="mt-1 text-sm text-red-600">{errors.longDescription.message}</p>
              )}
            </div>

            {/* <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  {...register('todayDeals')}
                  type="checkbox"
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Today's Deals</span>
              </label>
            </div> */}

            <div className="flex items-center space-x-8">
               {/* <label className="flex items-center space-x-2">
                <input
                  {...register('todayDeals')}
                  type="checkbox"
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">Today's Deals</span>
              </label> */}

              <label className="flex items-center space-x-2">
                <input
                  {...register('outofstock')}
                  type="checkbox"
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-800">Mark as Out of Stock</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  {...register('hideFromUser')}
                  type="checkbox"
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-800">Hide product</span>
              </label>
            </div>


            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  if (window.history.state?.modalOpen) {
                    window.history.back();
                  }
                  onClose();
                }}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Selection Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center p-4">
          <div onClick={() => setShowImageModal(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Select, Search, or Upload Images</h3>
                <button 
                  onClick={() => setShowImageModal(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div>
                {/* Upload Section */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Upload New Image</h4>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                        ) : (
                          <>
                            <PlusIcon className="w-8 h-8 mb-2 text-gray-500" />
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span>
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG or JPEG (MAX. 5MB)</p>
                            {selectedImages.length >= MAX_PRODUCT_IMAGES && (
                              <p className="text-xs text-orange-600 mt-1">
                                Note: You can select max {MAX_PRODUCT_IMAGES} image(s) per product
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                </div>

                {/* Food image search (stock photos) */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Search Food Images</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Find stock photos by name. Selected images open the crop editor, then upload to your library like file uploads.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 mb-4">
                    <input
                      type="text"
                      value={foodSearchQuery}
                      onChange={(e) => setFoodSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleFoodImageSearch();
                        }
                      }}
                      placeholder="e.g. pizza, biryani, burger"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={foodSearchLoading || uploadingImage}
                    />
                    <button
                      type="button"
                      onClick={handleFoodImageSearch}
                      disabled={foodSearchLoading || uploadingImage || !foodSearchQuery.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
                    >
                      {foodSearchLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  {foodSearchError && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{foodSearchError}</p>
                    </div>
                  )}
                  {foodSearchLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Searching images...</span>
                    </div>
                  ) : foodSearchResults.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {foodSearchResults.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleFoodImageSelect(item)}
                          disabled={uploadingImage || importingFoodImageId !== null}
                          className={`relative rounded-lg overflow-hidden border-2 text-left transition-all ${
                            importingFoodImageId === item.id
                              ? 'border-orange-500 ring-2 ring-orange-200'
                              : 'border-gray-300 hover:border-orange-400 cursor-pointer'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          title={item.alt || 'Select image'}
                        >
                          <img
                            src={item.url}
                            alt={item.alt || 'Food image'}
                            className="w-full h-24 object-cover"
                          />
                          {importingFoodImageId === item.id && (
                            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                            </div>
                          )}
                          {item.photographer && (
                            <p className="text-[10px] text-gray-500 px-1 py-0.5 truncate" title={item.photographer}>
                              {item.photographer}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {/* Existing Images Section */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Choose from Existing Images</h4>
                  {selectedImages.length >= MAX_PRODUCT_IMAGES && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">You can select maximum {MAX_PRODUCT_IMAGES} image(s) per product. You can still upload unlimited images to your library.</p>
                    </div>
                  )}
                  
                  {existingImagesLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading images...</span>
                    </div>
                  ) : existingImagesError ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{existingImagesError}</p>
                    </div>
                  ) : existingImages.length === 0 ? (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">No existing images found. Upload a new image to get started.</p>
                    </div>
                  ) : (
                                         <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                       {existingImages.map((image, index) => (
                         <div
                           key={index}
                           className={`relative rounded-lg overflow-hidden border-2 transition-all group ${
                             selectedImages.includes(image)
                               ? 'border-orange-500 ring-2 ring-orange-200'
                               : selectedImages.length >= MAX_PRODUCT_IMAGES && !selectedImages.includes(image)
                               ? 'border-gray-200 opacity-50 cursor-not-allowed'
                               : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                           }`}
                           onClick={() => selectedImages.length < MAX_PRODUCT_IMAGES || selectedImages.includes(image) ? handleImageSelect(image) : null}
                         >
                           {imageErrors.has(image) ? (
                             <div className="w-full h-20 flex items-center justify-center bg-gray-200">
                               <span className="text-xs text-gray-500">Image not found</span>
                             </div>
                           ) : (
                             <img
                               src={image}
                               alt={`Option ${index + 1}`}
                               onError={() => handleImageError(image)}
                               className="w-full h-20 object-cover"
                             />
                           )}
                           
                           {/* Selection indicator */}
                           {selectedImages.includes(image) && (
                             <div className="absolute inset-0 bg-orange-500 bg-opacity-20 flex items-center justify-center">
                               <div className="bg-orange-500 text-white rounded-full p-1">
                                 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                   <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                 </svg>
                               </div>
                             </div>
                           )}
                           
                           {/* Delete button */}
                           <button
                             type="button"
                             onClick={(e) => {
                               e.stopPropagation();
                               handleImageDelete(image);
                             }}
                             disabled={deletingImage === image}
                             className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                             title="Delete image"
                           >
                             {deletingImage === image ? (
                               <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                             ) : (
                               <TrashIcon className="h-3 w-3" />
                             )}
                           </button>
                         </div>
                       ))}
                     </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCropper && cropImageSrc && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Crop Product Image
              </h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="relative w-full h-72 sm:h-96 bg-gray-900 flex-shrink-0">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={4/3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                objectFit="contain"
              />
            </div>
            <div className="px-4 py-4 border-t flex-shrink-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-600 whitespace-nowrap min-w-[50px]">Zoom</span>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(event) => setZoom(Number(event.target.value))}
                      className="w-full accent-orange-600"
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-600 whitespace-nowrap min-w-[70px]">Rotation</span>
                    <input
                      type="range"
                      min={-180}
                      max={180}
                      step={1}
                      value={rotation}
                      onChange={(event) => setRotation(Number(event.target.value))}
                      className="w-full accent-orange-600"
                    />
                    <span className="text-sm text-gray-800 w-12 text-right">{`${rotation}°`}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t">
                  <button
                    onClick={handleCropCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                    disabled={uploadingImage}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Uploading...' : 'Save Crop'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductForm;